// Các key LocalStorage chuẩn hóa rõ ràng, có prefix của dự án PACS
export const tokenKey = 'pacs_token';
export const userTypeKey = 'pacs_user_type';
export const userUrlKey = 'pacs_return_url';
export const useNamelKey = 'pacs_username';
export const loginFailedCountKey = 'pacs_login_failed_count';
export const lastLoginFailedCountKey = 'pacs_last_login_failed_time';
export const shareQRtKey = 'pacs_share_qr';

/**
 * Cấu hình PACS API: Nạp hoàn toàn từ window.config.pacsApi (trong config/default.js hoặc app-config.js)
 * Không còn bất kỳ hardcode endpoint hay tài khoản nào trong mã nguồn này.
 */
export const getPacsConfig = () => {
  let cfg = {};
  try {
    if (typeof window !== 'undefined' && window.config && window.config.pacsApi) {
      cfg = window.config.pacsApi;
    }
  } catch (e) {
    console.error('[PACS_CONFIG] Lỗi khi đọc window.config.pacsApi:', e);
  }
  let host = cfg.host || '';
  if (host && !host.endsWith('/')) {
    host += '/';
  }
  let resourceHost = cfg.resourceHost || '';
  if (resourceHost && !resourceHost.endsWith('/')) {
    resourceHost += '/';
  }
  const resolved = {
    host,
    resourceHost,
    apikey: cfg.apikey || '',
    apiUserName: cfg.apiUserName || '',
    apiPassword: cfg.apiPassword || '',
  };
  if (!resolved.host) {
    console.warn(
      '[PACS_CONFIG] CẢNH BÁO: Chưa tìm thấy cấu hình pacsApi trong window.config (default.js)'
    );
  }
  return resolved;
};

/**
 * Hàm khởi tạo Header tập trung: Tránh lặp code và đảm bảo đủ 3 header
 */
const buildApiHeaders = (token = '', extraHeaders = {}) => {
  const { apikey, apiUserName, apiPassword } = getPacsConfig();
  const headers = new Headers();
  headers.append('api_key', apikey);
  headers.append('Content-Type', 'application/json');
  headers.append('Authorization', 'Basic ' + btoa(apiUserName + ':' + apiPassword));
  headers.append('client-security-token', 'Bearer ' + (token ?? ''));

  Object.entries(extraHeaders).forEach(([key, value]) => {
    if (value !== undefined && value !== null) {
      headers.append(key, value);
    }
  });

  return headers;
};

/**
 * Helper log API request / response trực quan cho console debug
 */
const logApiRequest = () => {};
const logApiResponse = () => {};
const logApiError = () => {};

/**
 * Xóa sạch toàn bộ thông tin phiên làm việc cục bộ
 */
const clearLocalSession = () => {
  try {
    localStorage.removeItem(tokenKey);
    localStorage.removeItem(userTypeKey);
    localStorage.removeItem(userUrlKey);
    localStorage.removeItem(shareQRtKey);
    localStorage.removeItem(loginFailedCountKey);
    localStorage.removeItem(lastLoginFailedCountKey);
  } catch (e) {
    console.error('[PACS_API] clearLocalSession error:', e);
  }
};

const checkVallidLoginTime = () => {
  let currentTime = Date.now();
  const lockoutDuration = 0 * 60 * 1000; // 5 minutes in milliseconds
  let lastLoginTime = currentTime;
  try {
    lastLoginTime = parseInt(localStorage.getItem(lastLoginFailedCountKey) || '0');
  } catch (e) {}
  let count = 0;
  try {
    count = parseInt(localStorage.getItem(loginFailedCountKey) || '0');
  } catch (e) {}
  if (count < 3) {
    return true;
  }
  if (currentTime - lastLoginTime > lockoutDuration) {
    localStorage.removeItem(loginFailedCountKey);
    return true;
  }
  localStorage.setItem(lastLoginFailedCountKey, currentTime.toString());
  return false;
};

const LoginApp = async (username, password) => {
  const raw = JSON.stringify({
    username: username,
    password: password,
  });

  try {
    const { host } = getPacsConfig();
    const myHeaders = buildApiHeaders();

    const requestOptions = {
      method: 'POST',
      headers: myHeaders,
      redirect: 'follow',
      body: raw,
    };

    const url = host + 'Pacs/login';
    logApiRequest('POST', url, myHeaders, raw);

    // @ts-ignore
    const response = await fetch(url, requestOptions);
    if (response.status === 401) {
      logApiResponse('POST', url, 401, 'Unauthorized');
      console.warn('[PACS_API] LoginApp received 401 Unauthorized');
      return {
        Status: 4,
        Message: 'Tài khoản hoặc mật khẩu không chính xác! Vui lòng kiểm tra lại.',
      };
    }
    const result = await response.json();
    logApiResponse('POST', url, response.status, result);
    return result;
  } catch (error) {
    logApiError('POST', (getPacsConfig().host || '') + 'Pacs/login', error);
    console.error('[PACS_API] LoginApp error:', error);
    return null;
  }
};

const fetchStudyList = async (patientid, deviceID) => {
  try {
    let token = '';
    try {
      token = (localStorage.getItem(tokenKey) || '')?.toString();
    } catch (e) {}

    const { host } = getPacsConfig();
    const myHeaders = buildApiHeaders(token, { deviceClient: deviceID });

    const requestOptions = {
      method: 'GET',
      headers: myHeaders,
      redirect: 'follow',
    };
    const url = host + 'Dicomapi/GetStudies?patientid=' + patientid;
    logApiRequest('GET', url, myHeaders);

    // @ts-ignore
    const response = await fetch(url, requestOptions);

    if (response.status === 401) {
      logApiResponse('GET', url, 401, 'Unauthorized (token invalid/expired)');
      console.warn('[PACS_API] fetchStudyList 401 -> returning status -2');
      return { status: -2, data: null };
    }
    const result = await response.json();
    logApiResponse('GET', url, response.status, result);
    if (result && result.status === -2) {
      return { status: -2, data: null };
    }
    if (result) {
      return result;
    }
    return { status: 0, data: null };
  } catch (error) {
    logApiError('GET', (getPacsConfig().host || '') + 'Dicomapi/GetStudies?patientid=' + patientid, error);
    console.error('[PACS_API] fetchStudyList EXCEPTION:', error);
    return { status: -1, data: null };
  }
};

const fetchStudyLists = async patientids => {
  try {
    let token = '';
    try {
      token = (localStorage.getItem(tokenKey) || '')?.toString();
    } catch (e) {}

    const { host } = getPacsConfig();
    const myHeaders = buildApiHeaders(token);

    const requestOptions = {
      method: 'GET',
      headers: myHeaders,
      redirect: 'follow',
    };
    const url = host + 'Dicomapi/GetStudies_V3?patientIds=' + patientids;
    logApiRequest('GET', url, myHeaders);

    // @ts-ignore
    const response = await fetch(url, requestOptions);

    if (response.status === 401) {
      logApiResponse('GET', url, 401, 'Unauthorized');
      console.warn('[PACS_API] fetchStudyLists 401 -> returning status -2');
      return { status: -2, data: null };
    }
    const result = await response.json();
    logApiResponse('GET', url, response.status, result);
    if (result && result.status === -2) {
      return { status: -2, data: null };
    }
    if (result) {
      return result;
    }
    return { status: 0, data: null };
  } catch (error) {
    logApiError('GET', (getPacsConfig().host || '') + 'Dicomapi/GetStudies_V3?patientIds=' + patientids, error);
    console.error('[PACS_API] fetchStudyLists EXCEPTION:', error);
    return { status: -1, data: null };
  }
};

const GeneratePublicPatientUrl = async duration => {
  try {
    const raw = JSON.stringify({
      duration: duration,
    });
    let token = '';
    try {
      token = (localStorage.getItem(tokenKey) || '')?.toString();
    } catch (e) {}

    const { host } = getPacsConfig();
    const myHeaders = buildApiHeaders(token);

    const requestOptions = {
      method: 'Post',
      headers: myHeaders,
      redirect: 'follow',
      body: raw,
    };
    const url = host + 'Dicomapi/GeneratePublicPatientUrl';
    logApiRequest('POST', url, myHeaders, raw);

    // @ts-ignore
    const response = await fetch(url, requestOptions);

    if (response.status === 401) {
      logApiResponse('POST', url, 401, 'Unauthorized');
      console.warn('[PACS_API] GeneratePublicPatientUrl 401 Unauthorized');
      return { status: -2, message: 'Lỗi 401: Unauthorized (Chưa xác thực hoặc sai token)' };
    }
    const result = await response.json();
    logApiResponse('POST', url, response.status, result);
    if (typeof result === 'string') {
      return { status: 1, data: result, message: 'Tạo QRCode thành công' };
    }
    if (result && (result.status === 1 || result.data)) {
      return {
        status: 1,
        data: result.data || result.shareUrl,
        message: result.message || 'Thành công',
      };
    }
    if (result && result.message) {
      return { status: result.status || 0, message: result.message };
    }
    return { status: 0, message: 'API trả về null hoặc rỗng' };
  } catch (error) {
    logApiError('POST', (getPacsConfig().host || '') + 'Dicomapi/GeneratePublicPatientUrl', error);
    console.error('[PACS_API] GeneratePublicPatientUrl Exception:', error);
    return { status: 0, message: 'Lỗi exception tại client: ' + (error?.message || error) };
  }
};

const LogOut = async () => {
  try {
    let token = '';
    try {
      token = (localStorage.getItem(tokenKey) || '')?.toString();
    } catch (e) {}

    const { host } = getPacsConfig();
    const myHeaders = buildApiHeaders(token);

    const requestOptions = {
      method: 'GET',
      headers: myHeaders,
      redirect: 'follow',
    };
    const url = host + 'Dicomapi/Logout';
    logApiRequest('GET', url, myHeaders);

    // @ts-ignore
    const response = await fetch(url, requestOptions);

    if (response.status === 401) {
      logApiResponse('GET', url, 401, 'Unauthorized');
      return -2;
    }
    const result = await response.json();
    logApiResponse('GET', url, response.status, result);
    if (result && result === true) {
      return 1;
    }

    return 0;
  } catch (error) {
    logApiError('GET', (getPacsConfig().host || '') + 'Dicomapi/Logout', error);
    console.error('[PACS_API] LogOut error:', error);
    return -1;
  }
};

const DeleteShreQR = async () => {
  try {
    let token = '';
    try {
      token = (localStorage.getItem(tokenKey) || '')?.toString();
    } catch (e) {}

    const { host } = getPacsConfig();
    const myHeaders = buildApiHeaders(token);

    const requestOptions = {
      method: 'GET',
      headers: myHeaders,
      redirect: 'follow',
    };
    const url = host + 'Dicomapi/DeleteShareQR';
    logApiRequest('GET', url, myHeaders);

    // @ts-ignore
    const response = await fetch(url, requestOptions);

    if (response.status === 401) {
      logApiResponse('GET', url, 401, 'Unauthorized');
      return -2;
    }
    const result = await response.json();
    logApiResponse('GET', url, response.status, result);
    if (result && result === true) {
      return 1;
    }

    return 0;
  } catch (error) {
    logApiError('GET', (getPacsConfig().host || '') + 'Dicomapi/DeleteShareQR', error);
    console.error('[PACS_API] DeleteShreQR error:', error);
    return -1;
  }
};

const logout = async () => {
  try {
    await LogOut();
  } catch (error) {
    console.error('[PACS_API] logout error during server call:', error);
  } finally {
    clearLocalSession();
  }
  return true;
};

const getToken = () => {
  try {
    return localStorage.getItem(tokenKey) || '';
  } catch (e) {}
  return '';
};

export {
  LoginApp,
  fetchStudyList,
  getToken,
  logout,
  checkVallidLoginTime,
  GeneratePublicPatientUrl,
  fetchStudyLists,
  DeleteShreQR,
  clearLocalSession,
};
